import { ConsumerEntry } from "../consumer-experience/ConsumerEntry";
/* eslint-disable react-refresh/only-export-components -- This file mixes the `appRoutes` route table (non-component) with dozens of `lazy()` component bindings; splitting would be a much larger refactor than the file deserves. */
import { lazy, Suspense } from "react";
import { RouteObject, Navigate } from "react-router-dom";
import { Loader } from "lucide-react";
import { useAppSelector } from '../hooks/useAppSelector';

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
    <Loader className="w-8 h-8 text-v2-neon animate-spin" />
  </div>
);

// Layouts carry authenticated navigation, search, notification, and compliance
// code. Keep them out of the public landing entry bundle.
const GeneralLayout = lazy(() => import("../layout/GeneralLayout").then(m => ({ default: m.GeneralLayout })));
const QuizFlowLayout = lazy(() => import("../layout/QuizFlowLayout").then(m => ({ default: m.QuizFlowLayout })));
const V2Layout = lazy(() => import("../v2/layouts/V2Layout").then(m => ({ default: m.V2Layout })));

const lazyLayout = (Layout: React.ComponentType<Record<string, unknown>>, props: Record<string, unknown> = {}) => (
  <Suspense fallback={<PageLoader />}>
    <Layout {...props} />
  </Suspense>
);

// Lazy load V2 pages
const FeedPage = lazy(() => import("../v2/pages/FeedPage").then(m => ({ default: m.FeedPage })));
const StartPage = lazy(() => import("../v2/pages/StartPage").then(m => ({ default: m.StartPage })));
const MatchInterceptScreen = lazy(() => import("../v2/pages/MatchInterceptScreen").then(m => ({ default: m.MatchInterceptScreen })));
const ContinuationPaywallPage = lazy(() => import("../v2/pages/ContinuationPaywallPage").then(m => ({ default: m.ContinuationPaywallPage })));
const InterceptQuizPreviewPage = lazy(() => import("../v2/pages/InterceptQuizPreviewPage").then(m => ({ default: m.InterceptQuizPreviewPage })));
const ProfilePage = lazy(() => import("../v2/pages/ProfilePage").then(m => ({ default: m.ProfilePage })));
const VehiclesPage = lazy(() => import("../v2/pages/profile/VehiclesPage").then(m => ({ default: m.VehiclesPage })));
const AddressesPage = lazy(() => import("../v2/pages/profile/AddressesPage").then(m => ({ default: m.AddressesPage })));
const V2ContactPage = lazy(() => import("../v2/pages/ContactPage").then(m => ({ default: m.ContactPage })));
const TermsPage = lazy(() => import("../v2/pages/TermsPage").then(m => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import("../v2/pages/PrivacyPage").then(m => ({ default: m.PrivacyPage })));
const DoNotSellPage = lazy(() => import("../v2/pages/DoNotSellPage").then(m => ({ default: m.DoNotSellPage })));
const V2NotificationsPage = lazy(() => import("../v2/pages/NotificationsPage").then(m => ({ default: m.NotificationsPage })));
const PersonalInfoPage = lazy(() => import("../v2/pages/PersonalInfoPage").then(m => ({ default: m.PersonalInfoPage })));
const PaymentMethodsPage = lazy(() => import("../v2/pages/PaymentMethodsPage").then(m => ({ default: m.PaymentMethodsPage })));
const ClaimsOverviewPage = lazy(() => import("../v2/pages/ClaimsOverviewPage").then(m => ({ default: m.ClaimsOverviewPage })));
const ClaimDetailPage = lazy(() => import("../v2/pages/ClaimDetailPage").then(m => ({ default: m.ClaimDetailPage })));
const AccidentEstimatePage = lazy(() => import("../v2/pages/AccidentEstimatePage").then(m => ({ default: m.AccidentEstimatePage })));
const UnclaimedPropertyPage = lazy(() => import("../v2/pages/UnclaimedPropertyPage").then(m => ({ default: m.UnclaimedPropertyPage })));
import { Features } from '../config/featureFlags';
const TrendingPage = lazy(() => import("../v2/pages/TrendingPage").then(m => ({ default: m.TrendingPage })));
const ClosingSoonPage = lazy(() => import("../v2/pages/ClosingSoonPage").then(m => ({ default: m.ClosingSoonPage })));
const ToolsPage = lazy(() => import("../v2/pages/ToolsPage").then(m => ({ default: m.ToolsPage })));
const SubscriptionPage = lazy(() => import("../v2/pages/SubscriptionPage").then(m => ({ default: m.SubscriptionPage })));
const SubscriptionReturnPage = lazy(() => import("../v2/pages/SubscriptionPage").then(m => ({ default: m.SubscriptionReturnPage })));
const FairdayIQDebugPage = lazy(() => import("../v2/pages/FairdayIQDebugPage").then(m => ({ default: m.FairdayIQDebugPage })));
const LandingPage = lazy(() => import("../v2/pages/LandingPage").then(m => ({ default: m.LandingPage })));
const AchievementsPage = lazy(() => import("../v2/pages/AchievementsPage").then(m => ({ default: m.AchievementsPage })));
const FamilyManagementPage = lazy(() => import("../v2/pages/FamilyManagementPage").then(m => ({ default: m.FamilyManagementPage })));
const ExternalRedirectPage = lazy(() => import("../v2/pages/ExternalRedirectPage").then(m => ({ default: m.ExternalRedirectPage })));
const PlaidOAuthPage = lazy(() => import("../v2/pages/PlaidOAuthPage").then(m => ({ default: m.PlaidOAuthPage })));
const ValueRevealPage = lazy(() => import("../v2/pages/ValueRevealPage").then(m => ({ default: m.ValueRevealPage })));
const CategoryDrillDownPage = lazy(() => import("../v2/pages/CategoryDrillDownPage").then(m => ({ default: m.CategoryDrillDownPage })));

// Agentic Beta Testing Page
const BetaAgentsPage = lazy(() => import("../v2/features/BetaAgents/pages/BetaAgentsPage").then(m => ({ default: m.BetaAgentsPage })));

// File for Me v2 — instant filing (consolidated from ClaimAgentPage)
const FileForMePage = lazy(() => import("../v2/pages/FileForMePage").then(m => ({ default: m.FileForMePage })));

// SEO Pages - Unclaimed Money
const UnclaimedMoneyDirectoryPage = lazy(() => import("../v2/pages/UnclaimedMoneyDirectoryPage").then(m => ({ default: m.UnclaimedMoneyDirectoryPage })));
const UnclaimedMoneyStatePage = lazy(() => import("../v2/pages/UnclaimedMoneyStatePage").then(m => ({ default: m.UnclaimedMoneyStatePage })));


// Lazy load legacy pages
const AboutUsScreen = lazy(() => import("../pages/about-us").then(m => ({ default: m.AboutUsScreen })));
const AccessibilityPage = lazy(() => import("../pages/accessibility").then(m => ({ default: m.AccessibilityPage })));
const AdvertisePage = lazy(() => import("../pages/advertise").then(m => ({ default: m.AdvertisePage })));
const AuthScreen = lazy(() => import("../pages/auth").then(m => ({ default: m.AuthScreen })));
const CaliforniaResidentsPage = lazy(() => import("../pages/california-residents").then(m => ({ default: m.CaliforniaResidentsPage })));
const Claim = lazy(() => import("../pages/claims/[id]").then(m => ({ default: m.Claim })));
const ClaimsPage = lazy(() => import("../pages/claims").then(m => ({ default: m.ClaimsPage })));
const ClaimSubmission = lazy(() => import("../pages/submit-claim").then(m => ({ default: m.ClaimSubmission })));
const ContactPage = lazy(() => import("../pages/contact").then(m => ({ default: m.ContactPage })));
const CookiesPolicyScreen = lazy(() => import("../pages/cookies").then(m => ({ default: m.CookiesPolicyScreen })));
const DeleteMyDataPage = lazy(() => import("../pages/delete-my-data").then(m => ({ default: m.DeleteMyDataPage })));
const Earnings = lazy(() => import("../pages/earnings").then(m => ({ default: m.Earnings })));
const FAQ = lazy(() => import("../pages/faqs").then(m => ({ default: m.FAQ })));
const PayoutFlow = lazy(() => import("../pages/payout-flow").then(m => ({ default: m.PayoutFlow })));
const PrivacyPolicyPage = lazy(() => import("../pages/privacy").then(m => ({ default: m.PrivacyPolicyPage })));
const QuizFlow = lazy(() => import("../pages/quiz").then(m => ({ default: m.QuizFlow })));
const SearchScreen = lazy(() => import("../pages/search").then(m => ({ default: m.SearchScreen })));
const TermsAndConditionsPage = lazy(() => import("../pages/terms").then(m => ({ default: m.TermsAndConditionsPage })));
const NewsletterUnsubscribe = lazy(() => import("../pages/newsletter-unsubscribe").then(m => ({ default: m.NewsletterUnsubscribe })));
const PartnersPage = lazy(() => import("../pages/partner-card").then(m => ({ default: m.PartnersPage })));
const AboutFairday = lazy(() => import("../pages/fairday-about-us"));
const VscFunnelPage = lazy(() => import("../v2/pages/vsc/VscFunnelPage").then(m => ({ default: m.VscFunnelPage })));

const SettlementsPage = lazy(() => import("../consumer-experience/SettlementsPage"));

const ConsumerFeedEntry = () => <ConsumerEntry mode="feed"><FeedPage /></ConsumerEntry>;

const RootEntry = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const rehydrated = useAppSelector((state) => Boolean((state.auth as { _persist?: { rehydrated?: boolean } })._persist?.rehydrated));

  // Public acquisition content renders immediately. Only redirect after the
  // persisted auth state is authoritative, avoiding a blank/loading entry path.
  if (rehydrated && isAuthenticated) return <Navigate to="/feed" replace />;
  return (
    <Suspense fallback={<PageLoader />}>
      <ConsumerEntry mode="landing"><LandingPage /></ConsumerEntry>
    </Suspense>
  );
};

// Wrap component with Suspense
const withSuspense = (Component: React.ComponentType) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
);

export const RoutesConfig: RouteObject[] = [
  { path: '/mobile/resume', element: withSuspense(lazy(() => import('../consumer-experience/MobileResumePage'))) },
  { path: '/claim-results', element: withSuspense(lazy(() => import('../consumer-experience/ClaimResultsPage'))) },
  { path: '/find', element: withSuspense(lazy(() => import('../consumer-experience/guided/GuidedFinderPage'))) },
  { path: '/check', element: withSuspense(lazy(() => import('../consumer-experience/landing/CheckLandingPage'))) },
  { path: '/go', element: withSuspense(lazy(() => import('../consumer-experience/landing/RotatePage'))) },
  { path: "/settlement-assistant", element: withSuspense(SettlementsPage) },
  {
    path: "/",
    element: <RootEntry />,
  },
  // Main authenticated routes - now using V2 pages
  {
    element: lazyLayout(V2Layout),
    children: [
      {
        path: "/feed",
        element: withSuspense(ConsumerFeedEntry),
      },
      {
        path: "profile",
        element: withSuspense(ProfilePage),
      },
      {
        path: "profile/vehicles",
        element: withSuspense(VehiclesPage),
      },
      {
        path: "profile/addresses",
        element: withSuspense(AddressesPage),
      },
      {
        path: "profile/family",
        element: withSuspense(FamilyManagementPage),
      },
      {
        path: "notifications",
        element: withSuspense(V2NotificationsPage),
      },
      {
        path: "personal-info",
        element: withSuspense(PersonalInfoPage),
      },
      {
        path: "payment-methods",
        element: withSuspense(PaymentMethodsPage),
      },
      {
        path: "claims-overview",
        element: withSuspense(ClaimsOverviewPage),
      },
      {
        path: "claim-details/:id",
        element: withSuspense(ClaimDetailPage),
      },
      {
        path: "file-for-me/:claimId",
        element: withSuspense(FileForMePage),
      },
      {
        path: "accident-estimate",
        element: withSuspense(AccidentEstimatePage),
      },
      // Feature-flagged: unclaimed property (re-enable via featureFlags.ts or PostHog)
      ...(Features.unclaimedProperty ? [{
        path: "unclaimed-property",
        element: withSuspense(UnclaimedPropertyPage),
      }] : []),
      {
        path: "trending",
        element: withSuspense(TrendingPage),
      },
      {
        path: "closing-soon",
        element: withSuspense(ClosingSoonPage),
      },
      {
        path: "tools",
        element: withSuspense(ToolsPage),
      },
      {
        path: "subscription",
        element: withSuspense(SubscriptionPage),
      },
      {
        path: "subscription/return",
        element: withSuspense(SubscriptionReturnPage),
      },
      {
        path: "family",
        element: withSuspense(FamilyManagementPage),
      },
      {
        path: "fairdayiq-debug",
        element: withSuspense(FairdayIQDebugPage),
      },
      {
        path: "search",
        element: withSuspense(SearchScreen),
      },
      {
        path: "achievements",
        element: withSuspense(AchievementsPage),
      },
      {
        path: "earnings",
        element: withSuspense(Earnings),
      },
      {
        path: "claims/:id",
        element: withSuspense(Claim),
      },
      {
        path: "submit-claim",
        element: withSuspense(ClaimSubmission),
      },
      {
        path: "payout",
        element: withSuspense(PayoutFlow),
      },
      {
        path: "terms",
        element: withSuspense(TermsPage),
      },
      {
        path: "privacy",
        element: withSuspense(PrivacyPage),
      },
      {
        path: "do-not-sell",
        element: withSuspense(DoNotSellPage),
      },
      {
        path: "contact",
        element: withSuspense(V2ContactPage),
      },
    ],
  },
  {
    element: lazyLayout(GeneralLayout),
    children: [
      {
        path: "/auth",
        element: withSuspense(AuthScreen),
      },
      {
        path: "/newsletterUnsubscribe",
        element: withSuspense(NewsletterUnsubscribe),
      },
      {
        path: "/faqs",
        element: withSuspense(FAQ),
      },
      {
        path: "/about-us",
        element: withSuspense(AboutUsScreen),
      },
      {
        path: "/contact",
        element: withSuspense(ContactPage),
      },
      {
        path: "/advertise",
        element: withSuspense(AdvertisePage),
      },
      {
        path: "/accessibility",
        element: withSuspense(AccessibilityPage),
      },
      {
        path: "/california-residents",
        element: withSuspense(CaliforniaResidentsPage),
      },
      {
        path: "/terms-conditions",
        element: withSuspense(TermsAndConditionsPage),
      },
      {
        path: "/privacy-policy",
        element: withSuspense(PrivacyPolicyPage),
      },
      {
        path: "/claims",
        element: withSuspense(ClaimsPage),
      },
      {
        path: "/delete-my-data",
        element: withSuspense(DeleteMyDataPage),
      },
      {
        path: "/cookies-policy",
        element: withSuspense(CookiesPolicyScreen),
      },
      {
        path: "/partners",
        element: withSuspense(PartnersPage),
      },
    ],
  },
  {
    element: lazyLayout(GeneralLayout, { noContainer: true }),
    children: [
      {
        path: "/details/:id",
        element: withSuspense(ClaimDetailPage),
      },
      {
        path: "/fairday-about-us",
        element: withSuspense(AboutFairday),
      },
    ],
  },
  {
    element: lazyLayout(QuizFlowLayout),
    children: [
      {
        path: "/quiz",
        element: withSuspense(QuizFlow),
      },
    ],
  },
  // New bank-first onboarding flow (standalone, no layout)
  {
    path: "/start",
    element: withSuspense(StartPage),
  },
  // B2B intercept hero screen — reached when the quiz detected a
  // high-ticket mass-tort match. Slides up the RetainerModal.
  {
    path: "/start/intercept",
    element: withSuspense(MatchInterceptScreen),
  },
  // Full-flow PREVIEW of the new B2B intercept quiz. Staging-only
  // end-to-end smoke test — renders the designed Q1/Q2/Q3 content
  // without touching production questions.csv, then routes through
  // the real intercept + retainer + paywall plumbing (with fallback
  // stub data if no live Campaign matches).
  {
    path: "/start/preview",
    element: withSuspense(InterceptQuizPreviewPage),
  },
  // Continuation-loop paywall — reached after a retainer signing OR
  // when the intercept evaluation returned { intercept: false }.
  {
    path: "/start/paywall",
    element: withSuspense(ContinuationPaywallPage),
  },
  // Public landing page with intent selector
  {
    path: "/welcome",
    element: withSuspense(LandingPage),
  },
  // External redirect page (standalone, no layout)
  {
    path: "/redirect",
    element: withSuspense(ExternalRedirectPage),
  },
  // Plaid OAuth callback page (standalone, no layout)
  {
    path: "/oauth/plaid",
    element: withSuspense(PlaidOAuthPage),
  },
  // Agentic Beta Testing Route (standalone, no layout for testing)
  {
    path: "/beta-agents",
    element: withSuspense(BetaAgentsPage),
  },
  // AI Claim Filing Agent ("File for Me") — consolidated to FileForMePage
  {
    path: "/file-for-me/:claimId",
    element: withSuspense(FileForMePage),
  },
  // ===========================================
  // PERSONALIZED ONBOARDING - VALUE REVEAL FLOW
  // ===========================================
  // Value reveal page - shows all settlements organized by category
  {
    path: "/results",
    element: withSuspense(ValueRevealPage),
  },
  // Category drill-down page - shows all settlements in a category
  {
    path: "/settlements/category/:categoryId",
    element: withSuspense(CategoryDrillDownPage),
  },
  // ===========================================
  // SEO PAGES - SETTLEMENTS DIRECTORY
  // ===========================================
  // Redirect /settlements to /claims (settlements browsing)
  // Note: Full SEO settlement pages are served from fairday-seo-content site
  {
    path: "/settlements",
    element: <Navigate to="/claims" replace />,
  },
  {
    path: "/settlements/:slug",
    element: <Navigate to="/claims" replace />,
  },
  // ===========================================
  // SEO PAGES - UNCLAIMED MONEY BY STATE
  // ===========================================
  // Directory page listing all 50 states + DC
  {
    path: "/unclaimed-money",
    element: withSuspense(UnclaimedMoneyDirectoryPage),
  },
  // Individual state pages (e.g., /unclaimed-money/california)
  {
    path: "/unclaimed-money/:state",
    element: withSuspense(UnclaimedMoneyStatePage),
  },
  // ===========================================
  // SPANISH LANGUAGE ROUTES (/es prefix)
  // ===========================================
  // Main authenticated routes - Spanish
  {
    path: "/es",
    element: lazyLayout(V2Layout),
    children: [
      {
        index: true,
        element: withSuspense(ConsumerFeedEntry),
      },
      {
        path: "feed",
        element: withSuspense(ConsumerFeedEntry),
      },
      {
        path: "profile",
        element: withSuspense(ProfilePage),
      },
      {
        path: "profile/vehicles",
        element: withSuspense(VehiclesPage),
      },
      {
        path: "profile/addresses",
        element: withSuspense(AddressesPage),
      },
      {
        path: "profile/family",
        element: withSuspense(FamilyManagementPage),
      },
      {
        path: "notifications",
        element: withSuspense(V2NotificationsPage),
      },
      {
        path: "personal-info",
        element: withSuspense(PersonalInfoPage),
      },
      {
        path: "payment-methods",
        element: withSuspense(PaymentMethodsPage),
      },
      {
        path: "claims-overview",
        element: withSuspense(ClaimsOverviewPage),
      },
      {
        path: "claim-details/:id",
        element: withSuspense(ClaimDetailPage),
      },
      {
        path: "file-for-me/:claimId",
        element: withSuspense(FileForMePage),
      },
      {
        path: "accident-estimate",
        element: withSuspense(AccidentEstimatePage),
      },
      // Feature-flagged: unclaimed property (re-enable via featureFlags.ts or PostHog)
      ...(Features.unclaimedProperty ? [{
        path: "unclaimed-property",
        element: withSuspense(UnclaimedPropertyPage),
      }] : []),
      {
        path: "trending",
        element: withSuspense(TrendingPage),
      },
      {
        path: "closing-soon",
        element: withSuspense(ClosingSoonPage),
      },
      {
        path: "tools",
        element: withSuspense(ToolsPage),
      },
      {
        path: "subscription",
        element: withSuspense(SubscriptionPage),
      },
      {
        path: "subscription/return",
        element: withSuspense(SubscriptionReturnPage),
      },
      {
        path: "family",
        element: withSuspense(FamilyManagementPage),
      },
      {
        path: "fairdayiq-debug",
        element: withSuspense(FairdayIQDebugPage),
      },
      {
        path: "search",
        element: withSuspense(SearchScreen),
      },
      {
        path: "achievements",
        element: withSuspense(AchievementsPage),
      },
      {
        path: "earnings",
        element: withSuspense(Earnings),
      },
      {
        path: "claims/:id",
        element: withSuspense(Claim),
      },
      {
        path: "submit-claim",
        element: withSuspense(ClaimSubmission),
      },
      {
        path: "payout",
        element: withSuspense(PayoutFlow),
      },
      {
        path: "terms",
        element: withSuspense(TermsPage),
      },
      {
        path: "privacy",
        element: withSuspense(PrivacyPage),
      },
      {
        path: "do-not-sell",
        element: withSuspense(DoNotSellPage),
      },
      {
        path: "contact",
        element: withSuspense(V2ContactPage),
      },
    ],
  },
  // General layout Spanish routes
  {
    path: "/es",
    element: lazyLayout(GeneralLayout),
    children: [
      {
        path: "auth",
        element: withSuspense(AuthScreen),
      },
      {
        path: "newsletterUnsubscribe",
        element: withSuspense(NewsletterUnsubscribe),
      },
      {
        path: "faqs",
        element: withSuspense(FAQ),
      },
      {
        path: "about-us",
        element: withSuspense(AboutUsScreen),
      },
      {
        path: "advertise",
        element: withSuspense(AdvertisePage),
      },
      {
        path: "accessibility",
        element: withSuspense(AccessibilityPage),
      },
      {
        path: "california-residents",
        element: withSuspense(CaliforniaResidentsPage),
      },
      {
        path: "terms-conditions",
        element: withSuspense(TermsAndConditionsPage),
      },
      {
        path: "privacy-policy",
        element: withSuspense(PrivacyPolicyPage),
      },
      {
        path: "delete-my-data",
        element: withSuspense(DeleteMyDataPage),
      },
      {
        path: "cookies-policy",
        element: withSuspense(CookiesPolicyScreen),
      },
      {
        path: "partners",
        element: withSuspense(PartnersPage),
      },
    ],
  },
  {
    path: "/es",
    element: lazyLayout(GeneralLayout, { noContainer: true }),
    children: [
      {
        path: "details/:id",
        element: withSuspense(ClaimDetailPage),
      },
      {
        path: "fairday-about-us",
        element: withSuspense(AboutFairday),
      },
    ],
  },
  {
    path: "/es",
    element: lazyLayout(QuizFlowLayout),
    children: [
      {
        path: "quiz",
        element: withSuspense(QuizFlow),
      },
    ],
  },
  // Standalone Spanish routes
  {
    path: "/es/start",
    element: withSuspense(StartPage),
  },
  {
    path: "/es/welcome",
    element: withSuspense(LandingPage),
  },
  {
    path: "/es/redirect",
    element: withSuspense(ExternalRedirectPage),
  },
  // Spanish SEO pages - Settlements
  {
    path: "/es/settlements",
    element: <Navigate to="/es/claims" replace />,
  },
  {
    path: "/es/settlements/:slug",
    element: <Navigate to="/es/claims" replace />,
  },
  // Spanish SEO pages - Unclaimed Money
  {
    path: "/es/unclaimed-money",
    element: withSuspense(UnclaimedMoneyDirectoryPage),
  },
  {
    path: "/es/unclaimed-money/:state",
    element: withSuspense(UnclaimedMoneyStatePage),
  },
  // Public VSC marketplace funnel — landing pages reachable from
  // partner ads. Standalone (no shared layout) for full creative control.
  {
    path: "/v/:slug",
    element: withSuspense(VscFunnelPage),
  },
];
